import React, { useEffect, useState, useRef } from 'react';
import { FiSearch, FiPrinter, FiMoreHorizontal, FiTruck, FiXCircle, FiExternalLink, FiCheck, FiMessageSquare, FiCopy, FiLayers, FiClock, FiMapPin, FiRefreshCw, FiAlertCircle, FiInbox } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../adminApi';
import { showAlert } from '../utils/dialog';

const PKT = 'Asia/Karachi';

function relativeDate(str) {
  const now = new Date();
  const d = new Date(str);
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const fmt = dt => dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: PKT }).toLowerCase();
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  if (hours < 24) return fmt(d);
  if (hours < 48) return `Yesterday at ${fmt(d)}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: PKT });
}

function payBadge(status) {
  if (status === 'delivered') return { label: 'Paid',            cls: 'obadge-green'  };
  if (status === 'cancelled') return { label: 'Voided',          cls: 'obadge-grey'   };
  if (status === 'return')    return { label: 'Refund pending',  cls: 'obadge-red'    };
  return                             { label: 'Payment pending', cls: 'obadge-yellow' };
}
function fulBadge(status) {
  if (status === 'delivered')       return { label: 'Fulfilled',        cls: 'obadge-green'  };
  if (status === 'shipped')         return { label: 'Shipped',          cls: 'obadge-blue'   };
  if (status === 'cancelled')       return { label: 'Cancelled',        cls: 'obadge-grey'   };
  if (status === 'undelivered')     return { label: 'Undelivered',      cls: 'obadge-orange' };
  if (status === 'return')          return { label: 'Return Submitted', cls: 'obadge-purple' };
  if (status === 'return_received') return { label: 'Return Received',  cls: 'obadge-cyan'   };
  return                                   { label: 'Unfulfilled',      cls: 'obadge-yellow' };
}

function AdminOrders() {
  const [orders,    setOrders]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [filter,    setFilter]    = useState('all');
  const [selected,  setSelected]  = useState(new Set());
  const [newOrderToast, setNewOrderToast] = useState(false);
  const prevOrderIds = useRef(new Set());
  const [dotsOpen,   setDotsOpen]   = useState(false);
  const [dotsPos,    setDotsPos]    = useState({ top: 0, left: 0 });
  const [markOpen,   setMarkOpen]   = useState(false);
  const [markPos,    setMarkPos]    = useState({ top: 0, left: 0 });
  const [trackModal,  setTrackModal]  = useState(null);  // { cn, events, loading }
  const [trackInput,  setTrackInput]  = useState(false);
  const [trackCN,     setTrackCN]     = useState('');
  const [syncing,     setSyncing]     = useState(false);

  const dotsRef = useRef(null);
  const dotsBtnRef = useRef(null);
  const markRef = useRef(null);
  const markBtnRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = e => {
      if (dotsRef.current && !dotsRef.current.contains(e.target)) setDotsOpen(false);
      if (markRef.current && !markRef.current.contains(e.target)) setMarkOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    adminApi.getOrders()
      .then(r => {
        const incoming = r.data;
        if (silent && prevOrderIds.current.size > 0) {
          const hasNew = incoming.some(o => !prevOrderIds.current.has(o.id));
          if (hasNew) {
            setNewOrderToast(true);
            setTimeout(() => setNewOrderToast(false), 5000);
          }
        }
        prevOrderIds.current = new Set(incoming.map(o => o.id));
        setOrders(incoming);
      })
      .catch(() => {})
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => {
    load();

    // SSE — refresh instantly when a new order is placed
    const stored = localStorage.getItem('seoul-admin');
    const token  = stored ? JSON.parse(stored)?.state?.token : null;
    let es = null;
    if (token) {
      es = new EventSource(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/admin/orders/stream?token=${token}`);
      es.onmessage = (e) => {
        if (e.data !== 'connected' && e.data !== 'ping') {
          load(true);
          setNewOrderToast(true);
          setTimeout(() => setNewOrderToast(false), 5000);
        }
      };
      es.onerror = () => es.close();
    }

    return () => { es?.close(); };
  }, []);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase().trim();
    const name = (o.user?.username || o.user?.full_name || o.guest_name || '').toLowerCase();
    const matchQ = !q ||
      String(o.id).includes(q) ||
      (o.order_number || '').toLowerCase().includes(q) ||
      name.includes(q) ||
      (o.phone || '').replace(/\s/g, '').includes(q.replace(/\s/g, '')) ||
      (o.delivery_address || '').toLowerCase().includes(q);
    return matchQ && (filter === 'all' || o.status === filter);
  });

  const allChecked = filtered.length > 0 && selected.size === filtered.length;
  const toggleAll  = () => setSelected(allChecked ? new Set() : new Set(filtered.map(o => o.id)));
  const allSelectedShipped = selected.size > 0 && orders.filter(o => selected.has(o.id)).every(o => o.status === 'shipped' || o.status === 'delivered' || o.status === 'cancelled');
  const toggleOne  = id => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const bulkStatus = async (status) => {
    setDotsOpen(false);
    const eligible = orders.filter(o => selected.has(o.id) && o.status !== 'shipped' && o.status !== 'delivered' && o.status !== 'cancelled');
    await Promise.all(eligible.map(o => adminApi.updateOrderStatus(o.id, status).catch(() => {})));
    setSelected(new Set());
    load();
  };

  const bulkMarkPaid = async () => {
    setMarkOpen(false);
    await adminApi.bulkMarkPaid([...selected]).catch(() => {});
    setSelected(new Set());
    load();
  };

  const printCNSlips = async () => {
    const toprint = orders.filter(o => selected.has(o.id) && o.postex_tracking && o.status === 'shipped');
    if (!toprint.length) return;
    const cns = toprint.map(o => o.postex_tracking);
    try {
      const res = await adminApi.getCNSlips(cns);
      const win = window.open('', '_blank', 'width=800,height=900');
      win.document.write(res.data.html);
      win.document.close();
      win.onload = () => { win.focus(); win.print(); };
    } catch {
      showAlert('Failed to fetch CN slips from CallCourier');
    }
  };

  const openTracking = async (orderId, cn) => {
    setTrackModal({ cn, events: [], loading: true });
    try {
      const res = await adminApi.getOrderTracking(orderId);
      setTrackModal({ cn, events: res.data.events || [], loading: false });
    } catch {
      setTrackModal({ cn, events: [], loading: false, error: true });
    }
  };

  const refreshStatus = async () => {
    setSyncing(true);
    try {
      await adminApi.syncTracking();
      load(true);
    } catch {}
    finally { setSyncing(false); }
  };

  const openManualTrack = async (e) => {
    e.preventDefault();
    const cn = trackCN.trim();
    if (!cn) return;
    setTrackInput(false);
    setTrackModal({ cn, events: [], loading: true });
    try {
      const res = await adminApi.trackByCN(cn);
      setTrackModal({ cn, events: res.data.events || [], loading: false });
    } catch {
      setTrackModal({ cn, events: [], loading: false, error: true });
    }
  };

  const openBookPage = () => {
    setDotsOpen(false);
    navigate('/orders/book', { state: { orderIds: [...selected] } });
  };

  const bulkDuplicate = async () => {
    setDotsOpen(false);
    const ids = [...selected];
    try {
      const results = await Promise.all(ids.map(id => adminApi.duplicateOrder(id).then(r => r.data.id)));
      setSelected(new Set());
      load(true);
      if (results.length === 1) {
        navigate(`/orders/${results[0]}`);
      } else {
        setFilter('pending');
      }
    } catch { showAlert('Duplicate failed'); }
  };

  const printShipping = async () => {
    const ids = [...selected];
    const results = await Promise.all(ids.map(id => adminApi.getOrder(id).then(r => r.data).catch(() => null)));
    const slips = results.filter(Boolean);
    if (!slips.length) return;
    const html = `<!DOCTYPE html><html><head><title>Shipping Labels</title><style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: Arial, sans-serif; font-size: 13px; color: #111; }
      .slip { width:100%; max-width:680px; margin:0 auto; padding:32px; page-break-after:always; border:2px solid #111; }
      .slip:last-child { page-break-after:avoid; }
      .slip-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; padding-bottom:16px; border-bottom:1px solid #ddd; }
      .store-name { font-size:22px; font-weight:800; }
      .store-name span { color:#f0b429; }
      .order-meta { text-align:right; }
      .order-meta .order-num { font-size:20px; font-weight:700; }
      .order-meta .order-date { font-size:11px; color:#666; margin-top:2px; }
      .section { margin-bottom:20px; }
      .section-title { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; color:#888; margin-bottom:8px; }
      .ship-to { font-size:15px; line-height:1.6; }
      .ship-to .name { font-size:17px; font-weight:700; margin-bottom:4px; }
      table { width:100%; border-collapse:collapse; }
      th { font-size:11px; font-weight:700; text-transform:uppercase; color:#666; padding:6px 8px; border-bottom:1px solid #ddd; text-align:left; }
      td { padding:7px 8px; border-bottom:1px solid #f0f0f0; }
      .right { text-align:right; }
      .totals { margin-top:12px; border-top:2px solid #111; padding-top:10px; }
      .total-row { display:flex; justify-content:space-between; padding:3px 8px; }
      .total-row.grand { font-size:16px; font-weight:700; margin-top:6px; border-top:1px solid #ddd; padding-top:8px; }
      @media print { body { margin:0; } }
    </style></head><body>
    ${slips.map(o => {
      const customer = o.user?.full_name || o.user?.username || o.guest_name || '—';
      const email    = o.user?.email || o.guest_email || '';
      const total    = o.total_amount + o.shipping_amount;
      const date     = new Date(o.created_at).toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric', timeZone: PKT });
      return `<div class="slip">
        <div class="slip-header">
          <div class="store-name">Seoul Station</div>
          <div class="order-meta"><div class="order-num">Order #${o.id}</div><div class="order-date">${date}</div></div>
        </div>
        <div class="section">
          <div class="section-title">Ship To</div>
          <div class="ship-to">
            <div class="name">${customer}</div>
            ${o.phone ? `<div>${o.phone}</div>` : ''}
            ${email   ? `<div>${email}</div>`   : ''}
            ${o.delivery_address ? `<div>${o.delivery_address}</div>` : ''}
          </div>
        </div>
        <div class="section">
          <div class="section-title">Items</div>
          <table><thead><tr><th>Product</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr></thead>
          <tbody>${o.items.map(i => `<tr><td>${i.name}</td><td class="right">${i.quantity}</td><td class="right">Rs ${i.price.toFixed(0)}</td><td class="right">Rs ${(i.price*i.quantity).toFixed(0)}</td></tr>`).join('')}</tbody></table>
        </div>
        <div class="totals">
          <div class="total-row"><span>Subtotal</span><span>Rs ${o.total_amount.toFixed(0)}</span></div>
          <div class="total-row"><span>Shipping</span><span>Rs ${o.shipping_amount.toFixed(0)}</span></div>
          <div class="total-row grand"><span>Total</span><span>Rs ${total.toFixed(0)}</span></div>
        </div>
      </div>`;
    }).join('')}</body></html>`;
    const win = window.open('', '_blank', 'width=750,height=900');
    win.document.write(html); win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  const TABS = ['all', 'pending', 'shipped', 'undelivered', 'delivered', 'cancelled', 'return', 'return_received'];
  const TAB_META = {
    all:             { label: 'All Orders',      icon: <FiLayers size={18} />,     desc: 'Every order across all statuses',    color: '#6366f1' },
    pending:         { label: 'Pending',          icon: <FiClock size={18} />,      desc: 'Awaiting confirmation',              color: '#f59e0b' },
    shipped:         { label: 'Shipped',          icon: <FiTruck size={18} />,      desc: 'Handed over to courier',             color: '#3b82f6' },
    undelivered:     { label: 'Undelivered',      icon: <FiAlertCircle size={18}/>, desc: 'Failed first delivery attempt',      color: '#f97316' },
    delivered:       { label: 'Delivered',        icon: <FiCheck size={18} />,      desc: 'Successfully delivered',             color: '#22c55e' },
    cancelled:       { label: 'Cancelled',        icon: <FiXCircle size={18} />,    desc: 'Cancelled orders',                   color: '#ef4444' },
    return:          { label: 'Return Submitted', icon: <FiRefreshCw size={18} />,  desc: 'Return submitted to courier',        color: '#8b5cf6' },
    return_received: { label: 'Return Received',  icon: <FiInbox size={18} />,      desc: 'Package received back',              color: '#0891b2' },
  };
  const counts = TABS.reduce((acc, t) => {
    acc[t] = t === 'all' ? orders.length : orders.filter(o => o.status === t).length;
    return acc;
  }, {});

  return (
    <div className="orders-wrap">
      {newOrderToast && (
        <div className="new-order-toast" onClick={() => { setNewOrderToast(false); setFilter('all'); }}>
          🛒 New order received!
        </div>
      )}

      <div className="orders-card">
        {/* Sub-header */}
        <div className="orders-card-header">
          <div>
            <div className="orders-card-title">Order Management</div>
            <div className="orders-card-subtitle">{orders.length} orders total</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="orders-track-btn" onClick={refreshStatus} disabled={syncing}>
              <FiRefreshCw size={14} className={syncing ? 'spin' : ''} /> {syncing ? 'Syncing…' : 'Refresh Status'}
            </button>
            <button className="orders-track-btn" onClick={() => { setTrackCN(''); setTrackInput(true); }}>
              <FiMapPin size={14} /> Track
            </button>
            <button className="orders-add-btn" onClick={() => navigate('/orders/new')}>
              + Add Order
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="orders-tabs-row">
          <div className="orders-tabs">
            {TABS.map(t => {
              const meta = TAB_META[t];
              const isActive = filter === t;
              return (
                <button key={t} className={`orders-tab ${isActive ? 'active' : ''}`} onClick={() => setFilter(t)} style={{ '--tc': meta.color }}>
                  <div className="otab-icon-box">{meta.icon}</div>
                  <div className="otab-body">
                    <div className="otab-top">
                      <span className="otab-label">{meta.label}</span>
                      <span className={`otab-count ${isActive ? 'otab-count-active' : ''}`}>{counts[t]}</span>
                    </div>
                    <div className="otab-desc">{meta.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="orders-search-row">
          <span className="orders-search-label">Search:</span>
          <div className="orders-search-box">
            <FiSearch size={13} color="#94a3b8" />
            <input placeholder="Order #, name, phone, or address" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Bulk action bar — shown when rows are selected */}
        {selected.size > 0 && (
          <div className="orders-bulk-bar">
            <span className="bulk-sel-count">{selected.size} selected</span>
            <div className="bulk-actions">
              <button className="bulk-btn bulk-btn-print" onClick={printShipping}>
                <FiPrinter size={13} /> Print
              </button>
              {!allSelectedShipped && (
                <div className="dots-wrap" ref={markRef}>
                  <button ref={markBtnRef} className="bulk-btn bulk-btn-mark"
                    onClick={() => {
                      if (!markOpen && markBtnRef.current) {
                        const r = markBtnRef.current.getBoundingClientRect();
                        setMarkPos({ top: r.bottom + 6, left: r.left });
                      }
                      setMarkOpen(o => !o);
                    }}>
                    <FiCheck size={13} /> Mark as <span className="btn-chevron">▾</span>
                  </button>
                  {markOpen && (
                    <div className="dots-menu" style={{ top: markPos.top, left: markPos.left }}>
                      <button className="dots-item dots-item-green" onClick={bulkMarkPaid}>
                        <FiCheck size={14} /> Paid
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button className="bulk-btn bulk-btn-cn" onClick={printCNSlips}>
                <FiPrinter size={13} /> Print CN
              </button>
              <div className="dots-wrap" ref={dotsRef}>
                <button ref={dotsBtnRef} className="bulk-btn bulk-btn-more"
                  onClick={() => {
                    if (!dotsOpen && dotsBtnRef.current) {
                      const r = dotsBtnRef.current.getBoundingClientRect();
                      setDotsPos({ top: r.bottom + 6, left: r.left });
                    }
                    setDotsOpen(o => !o);
                  }}>
                  <FiMoreHorizontal size={15} />
                </button>
                {dotsOpen && (
                  <div className="dots-menu" style={{ top: dotsPos.top, left: dotsPos.left }}>
                    <button className="dots-item" onClick={bulkDuplicate}>
                      <FiCopy size={14} /> Duplicate Order{selected.size > 1 ? 's' : ''}
                    </button>
                    {!allSelectedShipped && (
                      <button className="dots-item" onClick={openBookPage}>
                        <FiTruck size={14} /> Book with Courier
                      </button>
                    )}
                    {!allSelectedShipped && (
                      <button className="dots-item dots-item-danger" onClick={() => bulkStatus('cancelled')}>
                        <FiXCircle size={14} /> Cancel Order{selected.size > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="admin-table-wrap">
        {loading
          ? <div className="admin-loading"><div className="admin-spinner" />Loading…</div>
          : filtered.length === 0
            ? <div className="admin-empty">No orders found</div>
            : (
              <table className="orders-table">
                <thead>
                  <tr>
                    <th className="col-chk">
                      <input type="checkbox" checked={allChecked} onChange={toggleAll}
                        ref={el => el && (el.indeterminate = selected.size > 0 && !allChecked)} />
                    </th>
                    <th>Order</th><th>Date</th><th>Customer</th><th>Payment Type</th>
                    <th>Total</th><th>Fulfillment status</th>
                    <th>Items</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(o => {
                    const pay  = payBadge(o.status);
                    const ful  = fulBadge(o.status);
                    const name = o.user?.username || o.guest_name || '—';
                    return (
                      <tr
                        key={o.id}
                        className={`order-row ${selected.has(o.id) ? 'row-selected' : ''}`}
                        onClick={() => navigate(`/orders/${o.id}`)}
                      >
                        <td className="col-chk" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleOne(o.id)} />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className="order-link">#{o.id}</span>
                            {o.comments_count > 0 && (
                              <span className="order-comment-badge" title={`${o.comments_count} comment${o.comments_count !== 1 ? 's' : ''}`}>
                                <FiMessageSquare size={12} />
                                {o.comments_count}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="col-date">{relativeDate(o.created_at)}</td>
                        <td className="col-name">{name}</td>
                        <td>
                          {o.payment_method === 'paid' || o.payment_method === 'prepaid'
                            ? <span className="pay-type-badge pay-type-paid">Paid</span>
                            : <span className="pay-type-badge pay-type-cod">COD</span>
                          }
                        </td>
                        <td className="col-amount">
                          Rs {parseFloat(o.total_amount).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td>
                          <span className={`obadge ${ful.cls}`}><span className="obadge-dot"/>{ful.label}</span>
                          {o.tracking_status && o.status === 'undelivered' && (
                            <div className="tracking-status-pill">{o.tracking_status}</div>
                          )}
                        </td>
                        <td className="col-items">{o.items_count} {o.items_count === 1 ? 'item' : 'items'}</td>
                        <td onClick={e => e.stopPropagation()} className="col-actions">
                          <div className="order-action-btns">
                            {o.postex_tracking && (
                              <>
                                <button className="track-btn" onClick={() => openTracking(o.id, o.postex_tracking)}>
                                  <FiExternalLink size={12} /> Track
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
        }
        </div>{/* end admin-table-wrap */}
      </div>{/* end orders-card */}

      {/* Manual Track Input Modal */}
      {trackInput && (
        <div className="track-modal-overlay" onClick={() => setTrackInput(false)}>
          <div className="track-modal track-input-modal" onClick={e => e.stopPropagation()}>
            <div className="track-modal-header">
              <div>
                <h3 className="track-modal-title">Track Shipment</h3>
                <p className="track-modal-cn">Enter a CallCourier CN number</p>
              </div>
              <button className="track-modal-close" onClick={() => setTrackInput(false)}>✕</button>
            </div>
            <form className="track-input-form" onSubmit={openManualTrack}>
              <input
                autoFocus
                className="track-cn-input"
                placeholder="e.g. CC123456789"
                value={trackCN}
                onChange={e => setTrackCN(e.target.value)}
              />
              <button type="submit" className="btn-admin btn-admin-primary" disabled={!trackCN.trim()}>
                <FiMapPin size={14} /> Track
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      {trackModal && (
        <div className="track-modal-overlay" onClick={() => setTrackModal(null)}>
          <div className="track-modal" onClick={e => e.stopPropagation()}>
            <div className="track-modal-header">
              <div>
                <h3 className="track-modal-title">Tracking History</h3>
                <p className="track-modal-cn">CN: {trackModal.cn}</p>
              </div>
              <button className="track-modal-close" onClick={() => setTrackModal(null)}>✕</button>
            </div>
            <div className="track-modal-body">
              {trackModal.loading ? (
                <div className="admin-loading" style={{ padding: '24px 0' }}><div className="admin-spinner" /> Fetching…</div>
              ) : trackModal.error ? (
                <p className="track-modal-empty">Could not fetch tracking info.</p>
              ) : trackModal.events.length === 0 ? (
                <p className="track-modal-empty">No tracking events yet.</p>
              ) : (
                <div className="track-flow">
                  {trackModal.events.map((e, i, arr) => {
                    const desc   = e.ProcessDescForPortal || e.OperationDesc || e.Status || e.Activity || '—';
                    const loc    = e.OperationCity || e.City || '';
                    const time   = e.OperationDate || e.EventDate || e.Date || '';
                    const descUpper = desc.toUpperCase();
                    const isFailedEvent = ['UNDELIVER', 'RETURN', 'NOT DELIVER', 'FAILED', 'ATTEMPT', 'UNABLE'].some(k => descUpper.includes(k));
                    const reason = isFailedEvent && e.ReceiverName && e.ReceiverName.trim() && e.ReceiverName.trim() !== '-' ? e.ReceiverName.trim() : null;
                    const isCurrent = i === arr.length - 1;
                    return (
                      <div key={i} className="track-flow-item">
                        <div className={`track-flow-dot ${isCurrent ? 'dot-latest' : ''}`} />
                        <div className="track-flow-content">
                          <p className="track-flow-desc">{desc}{loc ? <span className="track-flow-loc"> · {loc}</span> : ''}</p>
                          {reason && <p className="track-flow-reason">{reason}</p>}
                          {time && <p className="track-flow-time">{time}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
